/**
 * Base error interface with common properties
 */ /**
 * Generic typed error class
 */ class TypedError extends Error {
    constructor(type, message, data = {}){
        super(message);
        this.name = "TypedError";
        this.type = type;
        this.data = data;
        // Add properties from data directly to the error for easier access
        for(const key in data){
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                this[key] = data[key];
            }
        }
    }
}
/**
 * Utility to convert PascalCase to kebab-case
 */ function pascalToKebab(str) {
    return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}
/**
 * Function to create an error factory
 */ function createErrorFactory(errorDefinitions) {
    // Process the error definitions to extract messages and schemas
    const errorMessages = {};
    const errorSchemas = {};
    Object.entries(errorDefinitions).forEach(([key, def])=>{
        if (typeof def === "string") {
            // Simple string definition
            errorMessages[key] = def;
        } else if (Array.isArray(def)) {
            // Tuple with message and optional schema
            errorMessages[key] = def[0];
            if (def.length > 1 && def[1]) {
                errorSchemas[key] = def[1];
            }
        }
    });
    // Create a map from PascalCase keys to kebab-case type strings
    const typeMap = new Map();
    // Create reverse map from kebab-case to PascalCase for lookups
    const reverseTypeMap = new Map();
    // Create a non-generic object first
    const nonGenericResult = {
        is: function isErrorType(error, type) {
            if (!(error instanceof Error) || !("type" in error)) {
                return false;
            }
            // Handle both PascalCase key and kebab-case string from Type constants
            let typeStr;
            if (typeof type === "string" && reverseTypeMap.has(type)) {
                // If it's a kebab-case string from Type constants, use it directly
                typeStr = type;
            } else {
                // Otherwise look it up in our map, treating it as a PascalCase key
                typeStr = typeMap.get(type) || type;
            }
            return error.type === typeStr;
        },
        isKnown: function isKnownErrorType(error) {
            if (!(error instanceof Error) || !("type" in error)) {
                return false;
            }
            // Check if the error's type is one of our known kebab-case types
            const allTypes = Array.from(typeMap.values());
            return allTypes.includes(error.type);
        },
        definitions: errorDefinitions,
        // Add a Type object to hold string literal constants
        Type: {}
    };
    // Process each error type to create kebab-case mappings and factory methods
    Object.keys(errorDefinitions).forEach((key)=>{
        // Store the PascalCase -> kebab-case mapping
        const kebabKey = pascalToKebab(key);
        typeMap.set(key, kebabKey);
        reverseTypeMap.set(kebabKey, key);
        // Get the schema for this error type
        const schema = errorSchemas[key] || {};
        const message = errorMessages[key];
        // Function to template message with data values
        function templateMessage(msg, data) {
            return msg.replace(/\{([^}]+)\}/g, (_, key)=>{
                return data[key] !== undefined ? String(data[key]) : `{${key}}`;
            });
        }
        // Create factory method using the original key (already PascalCase)
        nonGenericResult[key] = function(messageOrData, data) {
            // Check if we have schema fields defined for this error type
            const hasSchema = Object.keys(schema).length > 0;
            if (typeof messageOrData === "string") {
                // Called with custom message
                const errorData = data || {};
                // Template the custom message with data values
                const templatedMessage = templateMessage(messageOrData, errorData);
                return new TypedError(kebabKey, templatedMessage, errorData);
            } else if (messageOrData === undefined && !hasSchema) {
                // Called with no args for an error with no schema - use default message
                return new TypedError(kebabKey, message, {});
            } else {
                // Called with only data or no args for schema error
                const errorData = messageOrData || {};
                // Template the default message with data values
                const templatedMessage = templateMessage(message, errorData);
                return new TypedError(kebabKey, templatedMessage, errorData);
            }
        };
        // Add the type constant using the original key
        nonGenericResult.Type[key] = kebabKey;
    });
    return nonGenericResult;
}

export { TypedError, createErrorFactory };
